import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Loader2, Mail } from "lucide-react";
import { type FormEvent, useEffect, useId, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { Input, Label } from "@/components/ui/input";
import { AccountFrame } from "@/features/accounts/shared";
import { ApiError, authProvidersQuery, requestLoginLink } from "@/lib/api";

interface LoginSearch {
  next?: string;
  error?: string;
}

export const Route = createFileRoute("/login/")({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    next: typeof search.next === "string" ? search.next : undefined,
    error: typeof search.error === "string" ? search.error : undefined,
  }),
  component: LoginPage,
});

const GOOGLE_ERRORS: Record<string, string> = {
  google_cancelled: "Google sign-in was cancelled.",
  google_state: "That Google sign-in expired or came from another browser tab. Try again.",
  google_exchange: "Google didn't complete the sign-in. Try again.",
  google_token: "We couldn't verify that Google account. It needs a verified email address.",
};

const RESEND_AFTER_S = 30;

function GoogleMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className="size-4">
      <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5a5.6 5.6 0 0 1-2.4 3.6v3h3.9c2.2-2.1 3.5-5.1 3.5-8.8Z" />
      <path fill="#34A853" d="M12 24c3.2 0 6-1.1 8-2.9l-3.9-3c-1.1.7-2.5 1.2-4.1 1.2-3.1 0-5.8-2.1-6.7-5H1.3v3.1A12 12 0 0 0 12 24Z" />
      <path fill="#FBBC05" d="M5.3 14.3a7.2 7.2 0 0 1 0-4.6V6.6h-4a12 12 0 0 0 0 10.8l4-3.1Z" />
      <path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4A12 12 0 0 0 1.3 6.6l4 3.1c.9-2.9 3.6-4.9 6.7-4.9Z" />
    </svg>
  );
}

function LoginPage() {
  const { next, error } = Route.useSearch();
  const providers = useQuery(authProvidersQuery());
  const emailId = useId();
  const [email, setEmail] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [devLink, setDevLink] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [wait, setWait] = useState(0);

  useEffect(() => {
    document.title = "Sign in · traces.insure";
  }, []);
  useEffect(() => {
    if (wait <= 0) return;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const send = async (to: string) => {
    setPending(true);
    setFormError(null);
    try {
      const res = await requestLoginLink(to, next);
      setSentTo(to);
      setDevLink(res.devLink ?? null);
      setWait(RESEND_AFTER_S);
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Something went wrong. Try again.");
    } finally {
      setPending(false);
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const to = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) {
      setFormError("Enter a valid email address.");
      return;
    }
    void send(to);
  };

  const googleHref = `/api/v1/auth/google?next=${encodeURIComponent(next ?? "/")}`;

  return (
    <AccountFrame>
      <div className="mx-auto flex w-full max-w-sm flex-col gap-6 pt-6">
        <div className="flex flex-col gap-1">
          <h1 className="text-xl font-semibold tracking-tight">{sentTo ? "Check your email" : "Sign in to traces.insure"}</h1>
          <p className="text-[13px] text-fg-3">
            {sentTo ? (
              <>
                We sent a sign-in link to <span className="font-medium text-fg-2">{sentTo}</span>. It works once and expires in 15 minutes.
              </>
            ) : (
              "New here? Signing in creates your account; you'll set up your organization next."
            )}
          </p>
        </div>

        {error && GOOGLE_ERRORS[error] && !sentTo ? (
          <p role="alert" className="rounded-md border border-critical/40 bg-critical/10 px-3 py-2 text-[13px] text-critical-text">
            {GOOGLE_ERRORS[error]}
          </p>
        ) : null}

        <Card>
          <CardBody className="flex flex-col gap-4">
            {sentTo ? (
              <>
                <div className="flex items-center gap-3 rounded-md bg-surface-2 px-3 py-2.5 text-[13px] text-fg-2">
                  <Mail className="size-4 shrink-0 text-accent" aria-hidden />
                  Open the link on this device to finish signing in. Check spam if it doesn't arrive within a minute.
                </div>
                {devLink ? (
                  <a href={devLink} className="text-[13px] font-medium text-accent underline underline-offset-2">
                    Open the sign-in link (local development)
                  </a>
                ) : null}
                <div className="flex items-center justify-between gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSentTo(null);
                      setDevLink(null);
                    }}
                  >
                    <ArrowLeft /> Use a different email
                  </Button>
                  <Button variant="outline" size="sm" disabled={wait > 0 || pending} onClick={() => void send(sentTo)}>
                    {pending ? <Loader2 className="animate-spin" /> : null}
                    {wait > 0 ? `Resend in ${wait}s` : "Resend link"}
                  </Button>
                </div>
              </>
            ) : (
              <>
                {providers.data?.google ? (
                  <>
                    <Button variant="outline" asChild className="w-full">
                      <a href={googleHref}>
                        <GoogleMark /> Continue with Google
                      </a>
                    </Button>
                    <div className="flex items-center gap-3 text-[11px] uppercase tracking-wider text-fg-3" aria-hidden>
                      <span className="h-px flex-1 bg-border" /> or <span className="h-px flex-1 bg-border" />
                    </div>
                  </>
                ) : null}
                <form onSubmit={submit} noValidate className="flex flex-col gap-3">
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor={emailId}>Work email</Label>
                    <Input
                      id={emailId}
                      type="email"
                      autoComplete="email"
                      autoFocus
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      aria-invalid={formError ? true : undefined}
                      aria-describedby={formError ? `${emailId}-error` : undefined}
                    />
                    {formError ? (
                      <p id={`${emailId}-error`} className="text-[12px] text-critical-text">
                        {formError}
                      </p>
                    ) : null}
                  </div>
                  <Button type="submit" disabled={pending} className="w-full">
                    {pending ? <Loader2 className="animate-spin" /> : <Mail />} Email me a sign-in link
                  </Button>
                </form>
              </>
            )}
          </CardBody>
        </Card>
        <p className="text-center text-[11.5px] text-fg-3">
          By signing in you agree to the terms of service and privacy policy of traces.insure.
        </p>
      </div>
    </AccountFrame>
  );
}
